import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Check, Sparkles, Building2, User, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import subscriptionService from "../services/subscriptionService";
import { useNavigate } from "react-router-dom";
import { subscriptionPrices } from "../config/subscriptionPrices";

const advisorPrice = import.meta.env.VITE_REACT_ADVISOR_PRICE || '$99.99';
const analystPrice = import.meta.env.VITE_REACT_ANALYST_PRICE || '$99.99';

const plans = [
  {
    id: "investor",
    name: "Investor",
    price: "Free",
    period: "forever",
    description: "Perfect for individual investors",
    features: [
      "Portfolio tracking",
      "Social networking & messaging",
      "Matching algorithm",
      "Research reports (read-only)",
      "Profile & account management",
      "50 AI queries per month",
    ],
    cta: "Get Started Free",
    popular: false,
    highlight: "Free",
  },
  {
    id: "advisor",
    name: "Advisor",
    price: advisorPrice,
    period: "/month",
    description: "For professional financial advisors",
    features: [
      "Everything in Free",
      "Unlimited AI Assistant",
      "Client portfolio management",
      "Fee structure setup",
      "Portfolio analysis & reports",
      "30-day free trial",
    ],
    cta: "Start Free Trial",
    popular: false,
    highlight: "",
  },
  {
    id: "analyst",
    name: "Analyst",
    price: analystPrice,
    period: "/month",
    description: "For research analysts",
    features: [
      "Everything in Free",
      "Unlimited AI Assistant",
      "Research report generation",
      "Advanced analysis tools",
      "Data feeds & API access",
      "30-day free trial",
    ],
    cta: "Start Free Trial",
    popular: false,
    highlight: "",
  },
];

const ent1_10 = import.meta.env.VITE_REACT_ENT_1_10_PRICE || '$499.99/mo';
const ent11_50 = import.meta.env.VITE_REACT_ENT_11_50_PRICE || '$1,499.99/mo';
const ent51_200 = import.meta.env.VITE_REACT_ENT_51_200_PRICE || '$2,499.99/mo';
const ent201_1000 = import.meta.env.VITE_REACT_ENT_201_1000_PRICE || '$4,999.99/mo';

const enterpriseTiers = [
  { size: "1–10 employees", price: ent1_10 },
  { size: "11–50 employees", price: ent11_50 },
  { size: "51–200 employees", price: ent51_200 },
  { size: "201–1000 employees", price: ent201_1000 },
  { size: "1000+ employees", price: "Custom" },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (planId) => {
    if (planId === "investor") {
      navigate("/login?tab=signup");
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate(`/login?tab=signup&plan=${planId}`);
      return;
    }

    setLoading(planId);
    try {
      const response = await subscriptionService.createCheckout(planId.toUpperCase());
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, transparent pricing</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start free. Upgrade when you need more. All plans include a 30-day free trial for paid tiers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative flex flex-col ${plan.popular ? "border-primary shadow-lg ring-2 ring-primary" : ""}`}>
              {plan.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                  {plan.highlight}
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground ml-1">{plan.period}</span>}
                </div>
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex-col gap-2">
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id}
                  className="w-full"
                  variant="outline"
                >
                  {loading === plan.id ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="mr-2 h-4 w-4" />
                  )}
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                <CardTitle className="text-2xl">Enterprise</CardTitle>
              </div>
              <CardDescription className="text-lg">
                For organizations and teams. Includes all roles, admin dashboard, and consolidated billing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {enterpriseTiers.map((tier, i) => (
                  <div key={i} className="p-4 border rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">{tier.size}</p>
                    <p className="text-xl font-bold mt-1">{tier.price}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8 flex flex-col items-center gap-3">
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/login?tab=signup")}
                >
                  Start Free Trial
                </Button>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Contact us for custom pricing:</p>
                  <p>
                    <a href="mailto:info@wealthbrainai.com" className="text-primary hover:underline font-medium">
                      info@wealthbrainai.com
                    </a>
                  </p>
                  <p className="text-xs">Phone: (203) 722-2138</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12 text-sm text-muted-foreground">
          <p>All paid plans include a 30-day free trial. No credit card required.</p>
          <p className="mt-1">Need more time? <a href="mailto:support@wealthbrainai.com" className="text-primary hover:underline">Request extended trial</a></p>
        </div>
      </div>
    </div>
  );
}
